import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { 
  ArrowLeft, 
  Plus, 
  Truck, 
  Building2, 
  Phone, 
  Mail, 
  User, 
  Trash2, 
  Edit, 
  MapPin,
  Info,
  CheckCircle,
} from "lucide-react";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Textarea } from "@/components/ui/textarea";
import { createExternalPartySchema, type ExternalParty } from "@shared/schema";
import { z } from "zod";
import { useAuth } from "@/hooks/useAuth";

type FormData = z.infer<typeof createExternalPartySchema>;

export default function ExternalParties() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const { user } = useAuth();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingParty, setEditingParty] = useState<ExternalParty | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const { data: parties, isLoading } = useQuery<ExternalParty[]>({
    queryKey: ["/api/external-parties"],
    enabled: user?.userType === "Broker",
  });

  const form = useForm<FormData>({
    resolver: zodResolver(createExternalPartySchema),
    defaultValues: {
      partyType: "carrier",
      companyName: "",
      contactName: "",
      email: "",
      phone: "",
      dotNumber: "",
      mcNumber: "",
      address: "",
      notes: "",
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: FormData) => {
      return apiRequest("POST", "/api/external-parties", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/external-parties"] });
      setIsDialogOpen(false);
      form.reset();
      toast({
        title: "Contact added",
        description: "External party has been added to your contacts.",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to add external party. Please try again.",
        variant: "destructive",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<FormData> }) => {
      return apiRequest("PATCH", `/api/external-parties/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/external-parties"] });
      setIsDialogOpen(false);
      setEditingParty(null);
      form.reset();
      toast({
        title: "Contact updated",
        description: "External party has been updated.",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to update external party. Please try again.",
        variant: "destructive",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      return apiRequest("DELETE", `/api/external-parties/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/external-parties"] });
      setDeleteConfirm(null);
      toast({
        title: "Contact removed",
        description: "External party has been removed from your contacts.",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to remove external party. Please try again.",
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: FormData) => {
    if (editingParty) {
      updateMutation.mutate({ id: editingParty.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleEdit = (party: ExternalParty) => {
    setEditingParty(party);
    form.reset({
      partyType: party.partyType as "shipper" | "carrier",
      companyName: party.companyName,
      contactName: party.contactName || "",
      email: party.email || "",
      phone: party.phone || "",
      dotNumber: party.dotNumber || "",
      mcNumber: party.mcNumber || "",
      address: party.address || "",
      notes: party.notes || "",
    });
    setIsDialogOpen(true);
  };

  const handleAddNew = () => {
    setEditingParty(null);
    form.reset({
      partyType: "carrier",
      companyName: "",
      contactName: "",
      email: "",
      phone: "",
      dotNumber: "",
      mcNumber: "",
      address: "",
      notes: "",
    });
    setIsDialogOpen(true);
  };

  const carriers = parties?.filter(p => p.partyType === "carrier") || [];
  const shippers = parties?.filter(p => p.partyType === "shipper") || [];

  if (user?.userType !== "Broker") {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardContent className="p-6 text-center">
            <p className="text-muted-foreground">This feature is only available for Brokers.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-4xl">
      <div className="flex items-center gap-3 mb-6">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setLocation("/dashboard")}
          data-testid="button-back"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold" data-testid="text-page-title">External Contacts</h1>
          <p className="text-muted-foreground">
            Manage your off-platform shippers and carriers
          </p>
        </div>
      </div>

      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-muted-foreground">
              <p className="font-medium text-foreground mb-1">About External Contacts</p>
              <p>
                Add shippers and carriers who aren't on Fill The Trucks yet. When you assign them 
                to a shipment, they'll receive an email invitation to join the platform.
              </p>
              <p className="mt-2 font-medium text-foreground flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-600" />
                Your shipment is confirmed and active immediately - the invitation is optional and does not affect your deal.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold">Your Contacts</h2>
        <Button onClick={handleAddNew} data-testid="button-add-contact">
          <Plus className="h-4 w-4 mr-2" />
          Add Contact
        </Button>
      </div>

      {isLoading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-6">
                <div className="h-6 bg-muted rounded w-1/3 mb-2" />
                <div className="h-4 bg-muted rounded w-1/2" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="space-y-6">
          {carriers.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <Truck className="h-5 w-5" />
                Carriers ({carriers.length})
              </h2>
              <div className="grid gap-3">
                {carriers.map((party) => (
                  <ExternalPartyCard
                    key={party.id}
                    party={party}
                    onEdit={() => handleEdit(party)}
                    onDelete={() => setDeleteConfirm(party.id)}
                  />
                ))}
              </div>
            </div>
          )}

          {shippers.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                Shippers ({shippers.length})
              </h2>
              <div className="grid gap-3">
                {shippers.map((party) => (
                  <ExternalPartyCard
                    key={party.id}
                    party={party}
                    onEdit={() => handleEdit(party)}
                    onDelete={() => setDeleteConfirm(party.id)}
                  />
                ))}
              </div>
            </div>
          )}

          {(!parties || parties.length === 0) && (
            <Card>
              <CardContent className="p-8 text-center">
                <Building2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">No external contacts yet</h3>
                <p className="text-muted-foreground mb-4">
                  Add shippers and carriers who aren't on Fill The Trucks yet to assign them to your shipments.
                </p>
                <Button onClick={handleAddNew} data-testid="button-add-first-contact">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Your First Contact
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingParty ? "Edit Contact" : "Add External Contact"}
            </DialogTitle>
            <DialogDescription>
              {editingParty 
                ? "Update the contact details below." 
                : "Add a shipper or carrier who isn't on Fill The Trucks yet."}
            </DialogDescription>
          </DialogHeader>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="partyType"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Contact Type</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger data-testid="select-party-type">
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="carrier">Carrier</SelectItem>
                        <SelectItem value="shipper">Shipper</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="companyName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Company Name *</FormLabel>
                    <FormControl>
                      <Input 
                        placeholder="ABC Trucking Co." 
                        {...field} 
                        data-testid="input-company-name"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="contactName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Contact Name</FormLabel>
                    <FormControl>
                      <Input 
                        placeholder="John Smith" 
                        {...field}
                        value={field.value || ""}
                        data-testid="input-contact-name"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input 
                          type="email"
                          placeholder="email@company.com" 
                          {...field}
                          value={field.value || ""}
                          data-testid="input-email"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Phone</FormLabel>
                      <FormControl>
                        <Input 
                          placeholder="(555) 123-4567" 
                          {...field}
                          value={field.value || ""}
                          data-testid="input-phone"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {form.watch("partyType") === "carrier" && (
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="dotNumber"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>DOT Number</FormLabel>
                        <FormControl>
                          <Input 
                            placeholder="1234567" 
                            {...field}
                            value={field.value || ""}
                            data-testid="input-dot-number"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="mcNumber"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>MC Number</FormLabel>
                        <FormControl>
                          <Input 
                            placeholder="MC-123456" 
                            {...field}
                            value={field.value || ""}
                            data-testid="input-mc-number"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              )}

              <FormField
                control={form.control}
                name="address"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Address</FormLabel>
                    <FormControl>
                      <Input 
                        placeholder="123 Main St, City, State 12345" 
                        {...field}
                        value={field.value || ""}
                        data-testid="input-address"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="notes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Notes</FormLabel>
                    <FormControl>
                      <Textarea 
                        placeholder="Any additional notes about this contact..." 
                        {...field}
                        value={field.value || ""}
                        data-testid="input-notes"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsDialogOpen(false)}
                  data-testid="button-cancel"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  data-testid="button-save"
                >
                  {createMutation.isPending || updateMutation.isPending 
                    ? "Saving..." 
                    : editingParty 
                      ? "Update Contact" 
                      : "Add Contact"}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Contact?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove this external contact from your list. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-delete">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteConfirm && deleteMutation.mutate(deleteConfirm)}
              data-testid="button-confirm-delete"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function ExternalPartyCard({ 
  party, 
  onEdit, 
  onDelete 
}: { 
  party: ExternalParty; 
  onEdit: () => void; 
  onDelete: () => void;
}) {
  return (
    <Card className="hover-elevate" data-testid={`card-external-party-${party.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-semibold truncate" data-testid={`text-company-name-${party.id}`}>
                {party.companyName}
              </h3>
              <Badge variant="secondary" className="text-xs">
                {party.partyType === "carrier" ? "Carrier" : "Shipper"}
              </Badge>
              {party.invitationSent && (
                <Badge variant="outline" className="text-xs text-green-600">
                  Invited
                </Badge>
              )}
            </div>
            
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
              {party.contactName && (
                <span className="flex items-center gap-1">
                  <User className="h-3 w-3" />
                  {party.contactName}
                </span>
              )}
              {party.email && (
                <span className="flex items-center gap-1">
                  <Mail className="h-3 w-3" />
                  {party.email}
                </span>
              )}
              {party.phone && (
                <span className="flex items-center gap-1">
                  <Phone className="h-3 w-3" />
                  {party.phone}
                </span>
              )}
              {party.dotNumber && (
                <span>DOT: {party.dotNumber}</span>
              )}
              {party.mcNumber && (
                <span>MC: {party.mcNumber}</span>
              )}
            </div>

            {party.notes && (
              <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                {party.notes}
              </p>
            )}
          </div>

          <div className="flex gap-1 flex-shrink-0">
            <Button
              variant="ghost"
              size="icon"
              onClick={onEdit}
              data-testid={`button-edit-${party.id}`}
            >
              <Edit className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={onDelete}
              data-testid={`button-delete-${party.id}`}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
